import { Component, ComponentFactoryResolver, ComponentRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ReminderActionDefaultDataComponent } from 'src/app/controls/dynamic/reminder-action-default-data/reminder-action-default-data.component';
import { ReminderActionDisplayNotificationDataComponent } from 'src/app/controls/dynamic/reminder-action-display-notification-data/reminder-action-display-notification-data.component';
import { ReminderActionSendMailDataComponent } from 'src/app/controls/dynamic/reminder-action-send-mail-data/reminder-action-send-mail-data.component';
import { ReminderDataDirective } from 'src/app/controls/reminder-data.directive';
import { ReminderDataInterface } from 'src/app/models/reminder-data.interface';
import { ReminderActionType } from 'src/app/models/server/enums/reminder-action-type';
import { Reminder } from 'src/app/models/server/reminder';
import { EMailTemplatesService } from 'src/app/services/http/email-templates.service';
import { ReminderDataDirective as ReminderDataDirective_1 } from '../../../controls/reminder-data.directive';

import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SpinnerService } from 'src/app/services/core/spinner.service';

@Component({
    selector: 'app-add-reminder',
    templateUrl: './add-reminder.component.html',
    styleUrls: ['./add-reminder.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
        MatSelectModule,
        MatOptionModule,
        ReminderDataDirective_1,
        MatButtonModule,
        NgxSpinnerModule,
        MatCheckboxModule,
    ],
})
export class AddReminderComponent implements OnInit {
    public ReminderActionType = ReminderActionType;

    public frequencyPattern = new RegExp(/^(\d+[d,j]{1})?\s*(\d+[h]{1})?\s*(\d+[m]{1})?$/);

    private partRegex = new RegExp(/^(?<count>\d+)(?<unit>[d,j,h,m]{1})$/);

    public maxOccurencesPattern: string = '^[0-9]+$';

    /**
     * Champs du formulaire de saisie du nom.
     */
    public nameFormControl = new UntypedFormControl(null, Validators.required);
    public frequencyFormControl = new UntypedFormControl(null, [Validators.required, Validators.pattern(this.frequencyPattern)]);
    public maxOccurencesFormControl = new UntypedFormControl(null, Validators.pattern(this.maxOccurencesPattern));
    public firstOccurenceFormControl = new UntypedFormControl(null, Validators.required);
    public reminderActionTypeFormControl = new UntypedFormControl(null, Validators.required);
    public isRepeated = new UntypedFormControl(null);

    public get isValid(): boolean {
        return (
            this.nameFormControl.valid &&
            ((this.frequencyFormControl.valid && this.firstOccurenceFormControl.valid) ||
                (this.frequencyFormControl.valid && this.firstOccurenceFormControl.hasError('required')) ||
                (this.firstOccurenceFormControl.valid && this.frequencyFormControl.hasError('required'))) &&
            this.maxOccurencesFormControl.valid &&
            this.reminderActionTypeFormControl.valid &&
            this.componentRef &&
            this.componentRef.instance.isValid()
        );
    }

    @ViewChild(ReminderDataDirective, { static: true })
    reminderDataHost!: ReminderDataDirective;

    componentRef: ComponentRef<ReminderDataInterface>;

    /**
     * Constructeur.
     * @param dialogRef
     * @param data
     */
    constructor(
        public dialogRef: MatDialogRef<AddReminderComponent>,
        private componentFactoryResolver: ComponentFactoryResolver,
        private eMailTemplateService: EMailTemplatesService,
        private spinner: SpinnerService,
        @Inject(MAT_DIALOG_DATA) public data: Reminder
    ) {
        this.nameFormControl.setValue(data.name);
        this.frequencyFormControl.setValue(Reminder.formatTime(data.occurenceFrequency));
        this.maxOccurencesFormControl.setValue(data.maxOccurence);
        this.firstOccurenceFormControl.setValue(data.nextOccurenceDate ? data.nextOccurenceDate : new Date());
        this.reminderActionTypeFormControl.setValue(data.actionType);
        this.isRepeated.setValue(data.occurenceFrequency > 0);

        if (this.isRepeated.value) {
            this.frequencyFormControl.enable();
            this.maxOccurencesFormControl.enable();
        } else {
            this.frequencyFormControl.disable();
            this.maxOccurencesFormControl.disable();
        }

        this.isRepeated.valueChanges.subscribe((value) => {
            if (value) {
                this.frequencyFormControl.enable();
                this.maxOccurencesFormControl.enable();
            } else {
                this.frequencyFormControl.disable();
                this.maxOccurencesFormControl.disable();
            }
        });
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit() {
        this.loadDataComponent();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    public validateControl() {
        this.frequencyFormControl.updateValueAndValidity();
        this.firstOccurenceFormControl.updateValueAndValidity();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public validate(): void {
        if (this.isValid) {
            this.data.name = this.nameFormControl.value;
            this.data.occurenceFrequency = this.isRepeated.value ? this.getFrequencyDuration() : null;
            this.data.maxOccurence = this.isRepeated.value ? this.maxOccurencesFormControl.value : null;
            this.data.nextOccurenceDate = this.firstOccurenceFormControl.value;
            this.data.actionType = this.reminderActionTypeFormControl.value;
            this.data.actionData = this.componentRef.instance.data;
            this.dialogRef.close(this.data);
        }
    }

    public onReminderTypeChange(event: MatSelectChange) {
        this.loadDataComponent();
    }

    loadDataComponent() {
        const viewContainerRef = this.reminderDataHost.viewContainerRef;
        viewContainerRef.clear();

        if (this.reminderActionTypeFormControl.value) {
            switch (this.reminderActionTypeFormControl.value) {
                case ReminderActionType.DisplayNotification:
                    this.componentRef = viewContainerRef.createComponent<ReminderActionDisplayNotificationDataComponent>(
                        this.componentFactoryResolver.resolveComponentFactory(ReminderActionDisplayNotificationDataComponent)
                    );
                    break;
                case ReminderActionType.SendMail:
                    this.componentRef = viewContainerRef.createComponent<ReminderActionSendMailDataComponent>(
                        this.componentFactoryResolver.resolveComponentFactory(ReminderActionSendMailDataComponent)
                    );

                    this.loadEmailTemplates();
                    break;
                default:
                    this.componentRef = viewContainerRef.createComponent<ReminderActionDefaultDataComponent>(
                        this.componentFactoryResolver.resolveComponentFactory(ReminderActionDefaultDataComponent)
                    );
                    break;
            }

            if (this.data) {
                this.componentRef.instance.data = this.data.actionData;
            }
        }
    }

    loadEmailTemplates() {
        this.spinner.show('add-reminder-spinner');
        this.eMailTemplateService.List(this.data.viewId).subscribe((x) => {
            if (this.componentRef && this.componentRef.instance instanceof ReminderActionSendMailDataComponent) {
                (this.componentRef.instance as ReminderActionSendMailDataComponent).templates = x;
            }

            this.spinner.hide('add-reminder-spinner');
        });
    }

    // /**
    //  * Fonction permettant de valider que le la valeur d'un champ est un nombre entier.
    //  * @returns Une erreur si la valeur du champ n'est pas un nombre entier; null autrement.
    //  */
    // IsFilled(): ValidatorFn {
    //     return (control: AbstractControl): { [key: string]: any } | null => {
    //         if (this.frequencyFormControl && this.firstOccurenceFormControl) {
    //             if (!this.frequencyFormControl.value && (this.frequencyFormControl.dirty || this.frequencyFormControl.touched)) {
    //                 if (this.firstOccurenceFormControl.value) {
    //                     return null;
    //                 } else {
    //                     return { filledError: true };
    //                 }
    //             } else if (!this.firstOccurenceFormControl.value && (this.firstOccurenceFormControl.dirty || this.firstOccurenceFormControl.touched)) {
    //                 if (this.frequencyFormControl.value) {
    //                     return null;
    //                 } else {
    //                     return { filledError: true };
    //                 }
    //             } else {
    //                 return null;
    //             }
    //         } else {
    //             return null;
    //         }
    //     };
    // }

    /**
     * Emis lorsque la durée est modifiée (perte de focus)
     * @param $event Événement.
     */
    public onDurationChange($event): void {
        if (this.IsFrequencyDurationValid() === true) {
            this.frequencyFormControl.setErrors(null);
        } else {
            this.frequencyFormControl.setErrors({ pattern: true });
        }
    }

    /**
     * Retourne une valeur indiquant si la durée de mise en veille est valide.
     */
    private IsFrequencyDurationValid(): boolean {
        let isValid = true;

        const durationTextRepresentation: string = this.frequencyFormControl.value;

        if (!durationTextRepresentation) {
            return false;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                if (this.partRegex.test(part.trim()) === false) {
                    isValid = false;
                }
            });
        } else {
            isValid = false;
        }

        return isValid;
    }

    /**
     * Retourne la durée de fréquence en minutes
     */
    private getFrequencyDuration(): number {
        const durationTextRepresentation: string = this.frequencyFormControl.value;

        if (!durationTextRepresentation) {
            return null;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        let duration = 0;

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                let result = this.partRegex.exec(part.trim());
                const count = result.groups['count'];
                const unit = result.groups['unit'];

                switch (unit) {
                    case 'd':
                    case 'j':
                        duration += +count * (60 * 24);
                        break;
                    case 'h':
                        duration += +count * 60;
                        break;
                    case 'm':
                        duration += +count;
                        break;
                }
            });
        }

        return duration;
    }
}
